import { useState, useEffect } from "react";
import { gql } from "@apollo/client";
import { BigNumber } from "ethers";

import { getGmxGraphClient } from "lib/subgraph/clients";
import { bigNumberify, expandDecimals } from "lib/numbers";
import { USDG_DECIMALS } from "lib/legacy";
import { HourlyGlpStat } from "./types";
import { useChainId } from "../../lib/chains";

export type PlpPriceStoryItem = {
  timestamp: number;
  price: BigNumber;
};

export const PRECISION = "10000";
const DEFAULT_PLP_STATS_HOURS = 1000;

export function useHourlyPlpStats(hours = DEFAULT_PLP_STATS_HOURS) {
  const [plpPriceStory, setPlpPriceStory] = useState<PlpPriceStoryItem[]>([]);
  const { chainId } = useChainId();

  useEffect(() => {
    const getStats = async () => {
      const client = getGmxGraphClient(chainId);

      const query = gql`
        {
          hourlyGlpStats(
            first: ${hours},
            orderBy: id,
            orderDirection: desc,
          ) {
            id
            aumInUsdg
            glpSupply
          }
        }
      `;

      const res = await client!.query<{ hourlyGlpStats: HourlyGlpStat[] }>({ query });
      const hourlyGlpStats = res?.data?.hourlyGlpStats;

      if (hourlyGlpStats) {
        const prices = hourlyGlpStats.reduce((accumPrices: PlpPriceStoryItem[], plpStat) => {
          const aumInUsdg = bigNumberify(plpStat.aumInUsdg);
          const plpSupply = bigNumberify(plpStat.glpSupply);

          if (aumInUsdg && !aumInUsdg.isZero() && plpSupply && !plpSupply.isZero()) {
            const price = aumInUsdg.mul(PRECISION).div(plpSupply).mul(expandDecimals(1, USDG_DECIMALS)).div(PRECISION);
            return [...accumPrices, { timestamp: Number(plpStat.id), price }];
          }

          return accumPrices;
        }, []);

        setPlpPriceStory(prices);
      }
    };

    getStats();
  }, [hours, chainId]);

  return plpPriceStory;
}

export function usePlpPriceHistory() {
  const plpPriceStory = useHourlyPlpStats();

  return { plpPriceStory };
}
